import {
    ProjectTreeNode,
    TaskFilters,
    Project,
    TeamMember
} from '../components/shared/types';

// Baked in at build time from VITE_API_ORIGIN (see vite-env.d.ts).
// Staging and production set it to '' so the app calls its own origin and
// nginx proxies /api to the backend — one hostname, no CORS. Unset falls back
// to the local backend for `npm run dev`.
const DEFAULT_API_ORIGIN = 'http://127.0.0.1:3005';
const RESOLVED_API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? DEFAULT_API_ORIGIN;

const API_BASE_URL = `${RESOLVED_API_ORIGIN}/api`;

/** Backend origin without `/api` — for `/static/...` document paths. */
export const API_ORIGIN = RESOLVED_API_ORIGIN;

// --- Session ---------------------------------------------------------------

const TOKEN_KEY = 'rohan.admin.token';

export function getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
}

// The app registers a callback here so an expired or revoked token drops the
// user back to the sign-in screen from anywhere, without every caller having to
// handle 401 itself.
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
    onUnauthorized = handler;
}

/**
 * `fetch` with the bearer token attached and central 401 handling.
 *
 * Every request in this file goes through here — the backend now refuses
 * anonymous requests, so a call site that used bare `fetch` would simply fail.
 */
async function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
    const token = getToken();
    // Headers() rather than object spread, so an explicit Content-Type set by
    // the caller survives alongside the injected Authorization header.
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const response = await window.fetch(input, { ...init, headers });

    if (response.status === 401) {
        // The token is no longer good for anything; drop it so the next reload
        // shows the login screen rather than retrying in a loop.
        setToken(null);
        if (onUnauthorized) onUnauthorized();
    }

    return response;
}

/** Sign in and remember the token. */
export async function login(
    username: string,
    password: string,
): Promise<{ token: string; user: { id: number; name: string; username: string } }> {
    const response = await window.fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Sign in failed');
    }
    const data = await response.json();
    setToken(data.token);
    return data;
}

export function logout(): void {
    setToken(null);
}

/** Identity plus effective permissions, resolved server-side on every call. */
export type CurrentUser = {
    user: {
        id: number;
        name: string;
        username: string | null;
        job_title: string | null;
        phone: string | null;
        project_id: string | null;
    };
    access: {
        roles: string[];
        permissions: string[];
        projectIds: string[];
        allProjects: boolean;
    };
};

export async function fetchCurrentUser(): Promise<CurrentUser> {
    const response = await authFetch(`${API_BASE_URL}/auth/me`);
    if (!response.ok) throw new Error('Not signed in');
    return response.json();
}

// --- Access management -----------------------------------------------------

export type Permission = {
    id: number;
    key: string;
    name: string;
    description: string | null;
    category: string;
};

export type Role = {
    id: number;
    key: string;
    name: string;
    description: string | null;
    is_system: boolean;
    permissions: string[];
    assigned_users: number;
};

export type RoleAssignment = {
    assignment_id: number;
    role_id: number;
    role_key: string;
    role_name: string;
    project_id: string | null;
    project_name: string | null;
};

export type AccessUser = {
    id: number;
    name: string;
    username: string | null;
    job_title: string | null;
    phone: string | null;
    is_active: boolean;
    has_login: boolean;
    last_login_at: string | null;
    assignments: RoleAssignment[];
};

/** Unwraps the backend's `{ error }` body so the UI can show a real reason. */
async function readJson<T>(response: Response, fallback: string): Promise<T> {
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || fallback);
    }
    return response.json();
}

export const accessApi = {
    listPermissions: async (): Promise<Permission[]> =>
        readJson(await authFetch(`${API_BASE_URL}/access/permissions`), 'Failed to load permissions'),

    listRoles: async (): Promise<Role[]> =>
        readJson(await authFetch(`${API_BASE_URL}/access/roles`), 'Failed to load roles'),

    setRolePermissions: async (roleId: number, permissions: string[]): Promise<void> => {
        await readJson(
            await authFetch(`${API_BASE_URL}/access/roles/${roleId}/permissions`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ permissions }),
            }),
            'Failed to save permissions',
        );
    },

    listUsers: async (): Promise<AccessUser[]> =>
        readJson(await authFetch(`${API_BASE_URL}/access/users`), 'Failed to load users'),

    createUser: async (payload: {
        name: string;
        username: string;
        password: string;
        job_title?: string;
        phone?: string;
        assignments?: { role_id: number; project_id: string | null }[];
    }): Promise<{ id: number }> =>
        readJson(
            await authFetch(`${API_BASE_URL}/access/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            }),
            'Failed to create user',
        ),

    updateUser: async (
        id: number,
        payload: { name?: string; username?: string; job_title?: string; phone?: string; is_active?: boolean },
    ): Promise<void> => {
        await readJson(
            await authFetch(`${API_BASE_URL}/access/users/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            }),
            'Failed to update user',
        );
    },

    setUserRoles: async (
        id: number,
        assignments: { role_id: number; project_id: string | null }[],
    ): Promise<void> => {
        await readJson(
            await authFetch(`${API_BASE_URL}/access/users/${id}/roles`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ assignments }),
            }),
            'Failed to save role assignments',
        );
    },

    resetPassword: async (id: number, password: string): Promise<void> => {
        await readJson(
            await authFetch(`${API_BASE_URL}/access/users/${id}/password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password }),
            }),
            'Failed to reset password',
        );
    },

    revokeLogin: async (id: number): Promise<void> => {
        await readJson(
            await authFetch(`${API_BASE_URL}/access/users/${id}/login`, { method: 'DELETE' }),
            'Failed to revoke login',
        );
    },
};

export function resolvePublicFileUrl(filePath: string | null | undefined): string | null {
    if (filePath == null) return null;
    const t = String(filePath).trim();
    if (!t) return null;
    if (/^https?:\/\//i.test(t)) return t;
    const pathPart = t.startsWith('/') ? t : `/${t}`;
    return `${API_ORIGIN}${pathPart}`;
}

export interface MasterData {
    buildings: any[];
    floors: any[];
    series: any[];
    categories: any[];
    subcategories: any[];
}

export interface TreeNodeStats {
    total: number;
    completed: number;
    inProgress: number;
    planned: number;
    estimatedCost: number;
}


export interface TreeNodeStats {
    total: number;
    completed: number;
    inProgress: number;
    planned: number;
    estimatedCost: number;
}

export const api = {
    async getTasks(projectId?: string) {
        const url = projectId
            ? `${API_BASE_URL}/tasks?project_id=${projectId}`
            : `${API_BASE_URL}/tasks`;
        const response = await authFetch(url);
        if (!response.ok) {
            throw new Error('Failed to fetch tasks');
        }
        return response.json();
    },

    // Get all projects
    fetchProjects: async (): Promise<Project[]> => {
        const response = await authFetch(`${API_BASE_URL}/projects`);
        if (!response.ok) throw new Error('Failed to fetch projects');
        return response.json();
    },

    // Get team for a specific project
    fetchTeam: async (projectId: string): Promise<TeamMember[]> => {
        const response = await authFetch(`${API_BASE_URL}/projects/${projectId}/team`);
        if (!response.ok) {
            throw new Error('Failed to fetch team');
        }
        const data = await response.json();
        return data.map((item: any) => ({
            id: item.id,
            name: item.name,
            role: item.role,
            projectId: item.project_id,
            reportsTo: item.reports_to,
            managerName: item.manager_name,
            buildingId: item.building_id,
            buildingName: item.building_name
        }));
    },

    // Add team member
    addTeamMember: async (member: Partial<TeamMember>) => {
        const response = await authFetch(`${API_BASE_URL}/team`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name: member.name,
                role: member.role,
                project_id: member.projectId,
                reports_to: member.reportsTo,
                building_id: member.buildingId
            })
        });
        if (!response.ok) throw new Error('Failed to add team member');
        return response.json();
    },

    // Update team member
    updateTeamMember: async (id: number, member: Partial<TeamMember>) => {
        const response = await authFetch(`${API_BASE_URL}/team/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name: member.name,
                role: member.role,
                reports_to: member.reportsTo,
                building_id: member.buildingId
            })
        });
        if (!response.ok) throw new Error('Failed to update team member');
        return response.json();
    },

    // Delete team member
    deleteTeamMember: async (id: number): Promise<void> => {
        const response = await authFetch(`${API_BASE_URL}/team/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to delete team member');
        }
    },

    // Get manpower constants
    getManpowerConstants: async (projectId?: string): Promise<any[]> => {
        const url = projectId
            ? `${API_BASE_URL}/manpower-constants?project_id=${projectId}`
            : `${API_BASE_URL}/manpower-constants`;
        const response = await authFetch(url);
        if (!response.ok) throw new Error('Failed to fetch manpower constants');
        return response.json();
    },

    // Get master data (buildings, floors, etc.) - filtered by project
    getMasterData: async (projectId?: string): Promise<MasterData> => {
        const url = projectId
            ? `${API_BASE_URL}/master-data?project_id=${projectId}`
            : `${API_BASE_URL}/master-data`;
        const response = await authFetch(url);
        if (!response.ok) {
            throw new Error('Failed to fetch master data');
        }
        return response.json();
    },

    // Get hierarchical tree structure for a project
    // Structure: Building → Category → Subcategory → Floor → Unit
    // Optional filters scope the task counts so the tree matches the main view.
    getProjectTree: async (
        projectId: string,
        filters?: { dateStart?: string; dateEnd?: string; excludeCompleted?: boolean }
    ): Promise<ProjectTreeNode[]> => {
        const params = new URLSearchParams();
        if (filters?.dateStart) params.append('dateStart', filters.dateStart);
        if (filters?.dateEnd)   params.append('dateEnd',   filters.dateEnd);
        if (filters?.excludeCompleted) params.append('excludeCompleted', 'true');
        const qs = params.toString();
        const url = `${API_BASE_URL}/project-tree/${projectId}${qs ? `?${qs}` : ''}`;
        const response = await authFetch(url);
        if (!response.ok) {
            throw new Error('Failed to fetch project tree');
        }
        return response.json();
    },

    // Get tasks for a specific node (lazy loading)
    getTasksByNode: async (filters: TaskFilters): Promise<any[]> => {
        const params = new URLSearchParams();
        params.append('project_id', filters.project_id);

        if (filters.building_id) params.append('building_id', filters.building_id);
        if (filters.category_id) params.append('category_id', filters.category_id);
        if (filters.subcategory_id) params.append('subcategory_id', filters.subcategory_id);
        if (filters.floor_id) params.append('floor_id', filters.floor_id);
        if (filters.unit_id) params.append('unit_id', filters.unit_id);
        if (filters.milestone_id) params.append('milestone_id', filters.milestone_id);

        const response = await authFetch(`${API_BASE_URL}/tasks-by-node?${params.toString()}`);
        if (!response.ok) {
            throw new Error('Failed to fetch tasks');
        }
        return response.json();
    },

    // Update task planning dates (Monthly/Weekly)
    updateTaskPlan: async (taskIds: string[], monthlyPlanDate?: string | null, weeklyPlanDate?: string | null) => {
        const response = await authFetch(`${API_BASE_URL}/tasks/plan`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ taskIds, monthlyPlanDate, weeklyPlanDate })
        });
        if (!response.ok) throw new Error('Failed to update task plan');
        return response.json();
    },

    // Assign tasks by free-text name (legacy)
    assignTasks: async (taskIds: string[], assignedTo: string) => {
        const response = await authFetch(`${API_BASE_URL}/tasks/assign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ taskIds, assignedTo })
        });
        if (!response.ok) throw new Error('Failed to assign tasks');
        return response.json();
    },

    // Assign tasks to a real team_member (engineer) by id.
    assignTasksToMember: async (taskIds: (string | number)[], teamMemberId: number) => {
        const response = await authFetch(`${API_BASE_URL}/tasks/assign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ taskIds, assignedTeamMemberId: teamMemberId })
        });
        if (!response.ok) throw new Error('Failed to assign tasks to engineer');
        return response.json();
    },

    // ==========================================
    // WBS SYNC (admin)
    // ==========================================

    // Trigger WBS sync for one project by name.
    syncWbsProject: async (projectName: string): Promise<{
        runId: number;
        project: string;
        source: 'api' | 'fixture';
        rows_in: number;
        inserted: number;
        updated: number;
        unchanged: number;
    }> => {
        const response = await authFetch(
            `${API_BASE_URL}/admin/wbs/sync/${encodeURIComponent(projectName)}`,
            { method: 'POST' }
        );
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.error || 'WBS sync failed');
        return payload;
    },

    // Trigger WBS sync for every project in WBS_PROJECTS env.
    syncAllWbsProjects: async () => {
        const response = await authFetch(`${API_BASE_URL}/admin/wbs/sync`, { method: 'POST' });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.error || 'WBS sync failed');
        return payload;
    },

    // Last N sync runs (for the admin "last synced at" widget).
    getWbsSyncRuns: async (limit = 20): Promise<Array<{
        id: number;
        project_name: string;
        project_id: number | null;
        source: 'api' | 'fixture' | 'manual';
        triggered_by: string | null;
        started_at: string;
        finished_at: string | null;
        rows_in: number;
        rows_inserted: number;
        rows_updated: number;
        rows_unchanged: number;
        error: string | null;
    }>> => {
        const response = await authFetch(`${API_BASE_URL}/admin/wbs/runs?limit=${limit}`);
        if (!response.ok) throw new Error('Failed to fetch WBS sync runs');
        return response.json();
    },

    // Update task status
    updateTaskStatus: async (taskIds: string[], status: string) => {
        const response = await authFetch(`${API_BASE_URL}/tasks/status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ taskIds, status })
        });
        if (!response.ok) throw new Error('Failed to update task status');
        return response.json();
    },

    // Get resources for specific tasks
    getTaskResources: async (taskIds: string[]): Promise<any[]> => {
        const response = await authFetch(`${API_BASE_URL}/tasks/resources`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ taskIds })
        });
        if (!response.ok) throw new Error('Failed to fetch task resources');
        return response.json();
    },

    // ==========================================
    // MONTHLY PLANS API
    // ==========================================

    // Get all monthly plans for a project
    getMonthlyPlans: async (projectId: string, status?: string): Promise<MonthlyPlan[]> => {
        const url = status
            ? `${API_BASE_URL}/projects/${projectId}/monthly-plans?status=${status}`
            : `${API_BASE_URL}/projects/${projectId}/monthly-plans`;
        const response = await authFetch(url);
        if (!response.ok) throw new Error('Failed to fetch monthly plans');
        return response.json();
    },

    // Get single monthly plan with details
    getMonthlyPlan: async (planId: number): Promise<MonthlyPlanDetail> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plans/${planId}`);
        if (!response.ok) throw new Error('Failed to fetch monthly plan');
        return response.json();
    },

    // Create new monthly plan
    createMonthlyPlan: async (projectId: string, data: CreateMonthlyPlanData): Promise<MonthlyPlan> => {
        const response = await authFetch(`${API_BASE_URL}/projects/${projectId}/monthly-plans`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to create monthly plan');
        }
        return response.json();
    },

    // Update monthly plan
    updateMonthlyPlan: async (planId: number, data: Partial<MonthlyPlan>): Promise<MonthlyPlan> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plans/${planId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Failed to update monthly plan');
        return response.json();
    },

    // Publish monthly plan
    publishMonthlyPlan: async (planId: number): Promise<MonthlyPlan> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plans/${planId}/publish`, {
            method: 'POST'
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to publish monthly plan');
        }
        return response.json();
    },

    // Get incomplete tasks for rollover selection
    getIncompleteTasks: async (planId: number): Promise<IncompletePlanTask[]> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plans/${planId}/incomplete-tasks`);
        if (!response.ok) throw new Error('Failed to fetch incomplete tasks');
        return response.json();
    },

    // Close monthly plan with optional rollover selection
    closeMonthlyPlan: async (planId: number, options?: { rolloverTaskIds?: number[]; targetPlanId?: number }): Promise<MonthlyPlan & { rolledOverTasks: number; targetPlanId?: number }> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plans/${planId}/close`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(options || {})
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to close monthly plan');
        }
        return response.json();
    },

    // Delete monthly plan
    deleteMonthlyPlan: async (planId: number): Promise<void> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plans/${planId}`, {
            method: 'DELETE'
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to delete monthly plan');
        }
    },

    // Add tasks to monthly plan
    addTasksToMonthlyPlan: async (planId: number, taskIds: number[]): Promise<{ success: boolean; added: number; skipped: any[] }> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plans/${planId}/tasks`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ taskIds })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to add tasks to plan');
        }
        return response.json();
    },

    // Add rollover tasks to monthly plan
    addRolloverTasks: async (planId: number, sourcePlanId: number, taskIds?: number[]): Promise<{ success: boolean; added: number }> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plans/${planId}/rollover`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sourcePlanId, taskIds })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to add rollover tasks');
        }
        return response.json();
    },

    // Remove task from monthly plan
    removeTaskFromPlan: async (planTaskId: number): Promise<void> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plan-tasks/${planTaskId}`, {
            method: 'DELETE'
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to remove task from plan');
        }
    },

    // Update task in monthly plan
    updatePlanTask: async (planTaskId: number, data: { status?: string; notes?: string; end_progress?: number }): Promise<any> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plan-tasks/${planTaskId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Failed to update plan task');
        return response.json();
    },

    // Get available tasks for a monthly plan
    getAvailableTasks: async (planId: number, source: 'master' | 'rollover'): Promise<{ tasks: any[]; sourcePlanId?: number }> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plans/${planId}/available-tasks?source=${source}`);
        if (!response.ok) throw new Error('Failed to fetch available tasks');
        return response.json();
    },

    // ==========================================
    // WEEKLY PLANS API
    // ==========================================

    // Get all weekly plans for a monthly plan
    getWeeklyPlans: async (monthlyPlanId: number): Promise<WeeklyPlan[]> => {
        const response = await authFetch(`${API_BASE_URL}/monthly-plans/${monthlyPlanId}/weekly-plans`);
        if (!response.ok) throw new Error('Failed to fetch weekly plans');
        return response.json();
    },

    // Get all weekly plans for a project (across all monthly plans)
    getProjectWeeklyPlans: async (projectId: string): Promise<WeeklyPlan[]> => {
        const response = await authFetch(`${API_BASE_URL}/projects/${projectId}/weekly-plans`);
        if (!response.ok) throw new Error('Failed to fetch weekly plans');
        return response.json();
    },

    // Create a new weekly plan
    createWeeklyPlan: async (data: CreateWeeklyPlanData): Promise<WeeklyPlan> => {
        const response = await authFetch(`${API_BASE_URL}/weekly-plans`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to create weekly plan');
        }
        return response.json();
    },

    // Update weekly plan
    updateWeeklyPlan: async (planId: number, data: Partial<WeeklyPlan>): Promise<WeeklyPlan> => {
        const response = await authFetch(`${API_BASE_URL}/weekly-plans/${planId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Failed to update weekly plan');
        return response.json();
    },

    // Delete weekly plan
    deleteWeeklyPlan: async (planId: number): Promise<void> => {
        const response = await authFetch(`${API_BASE_URL}/weekly-plans/${planId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete weekly plan');
    },

    // Add tasks to weekly plan
    addTasksToWeeklyPlan: async (planId: number, planTaskIds: number[]): Promise<{ success: boolean; added: number; skipped: any[] }> => {
        const response = await authFetch(`${API_BASE_URL}/weekly-plans/${planId}/tasks`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ planTaskIds })
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to add tasks to weekly plan');
        }
        return response.json();
    },

    // Remove task from weekly plan
    removeTaskFromWeeklyPlan: async (weeklyPlanTaskId: number): Promise<void> => {
        const response = await authFetch(`${API_BASE_URL}/weekly-plan-tasks/${weeklyPlanTaskId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to remove task from weekly plan');
    },

    // Get weekly plan with tasks
    getWeeklyPlanDetail: async (planId: number): Promise<WeeklyPlanDetail> => {
        const response = await authFetch(`${API_BASE_URL}/weekly-plans/${planId}`);
        if (!response.ok) throw new Error('Failed to fetch weekly plan');
        return response.json();
    },

    // ==========================================
    // CONCERNS API
    // ==========================================

    // Get all concerns for a project
    getConcerns: async (projectId: number | string, filters?: { 
        status?: ConcernStatus; 
        type?: ConcernType; 
        monthly_plan_id?: number 
    }): Promise<Concern[]> => {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.type) params.append('type', filters.type);
        if (filters?.monthly_plan_id) params.append('monthly_plan_id', filters.monthly_plan_id.toString());
        
        const url = `${API_BASE_URL}/projects/${projectId}/concerns${params.toString() ? '?' + params.toString() : ''}`;
        const response = await authFetch(url);
        if (!response.ok) throw new Error('Failed to fetch concerns');
        return response.json();
    },

    // Get single concern with tasks
    getConcern: async (concernId: number): Promise<ConcernDetail> => {
        const response = await authFetch(`${API_BASE_URL}/concerns/${concernId}`);
        if (!response.ok) throw new Error('Failed to fetch concern');
        return response.json();
    },

    // Create a new concern
    createConcern: async (projectId: number | string, data: CreateConcernData): Promise<{ success: boolean; concern: Concern; linked_tasks: number }> => {
        const response = await authFetch(`${API_BASE_URL}/projects/${projectId}/concerns`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to create concern');
        }
        return response.json();
    },

    // Update a concern
    updateConcern: async (concernId: number, data: Partial<Pick<Concern, 'title' | 'description' | 'priority' | 'status' | 'concern_type'>>): Promise<Concern> => {
        const response = await authFetch(`${API_BASE_URL}/concerns/${concernId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new Error('Failed to update concern');
        return response.json();
    },

    // Acknowledge a concern
    acknowledgeConcern: async (concernId: number): Promise<Concern> => {
        const response = await authFetch(`${API_BASE_URL}/concerns/${concernId}/acknowledge`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error('Failed to acknowledge concern');
        return response.json();
    },

    // Resolve a concern
    resolveConcern: async (concernId: number, resolution_notes?: string): Promise<Concern> => {
        const response = await authFetch(`${API_BASE_URL}/concerns/${concernId}/resolve`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ resolution_notes })
        });
        if (!response.ok) throw new Error('Failed to resolve concern');
        return response.json();
    },

    // Close a concern
    closeConcern: async (concernId: number): Promise<Concern> => {
        const response = await authFetch(`${API_BASE_URL}/concerns/${concernId}/close`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error('Failed to close concern');
        return response.json();
    },

    // Delete a concern
    deleteConcern: async (concernId: number): Promise<void> => {
        const response = await authFetch(`${API_BASE_URL}/concerns/${concernId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete concern');
    },

    // Add tasks to a concern
    addTasksToConcern: async (concernId: number, task_ids: number[]): Promise<{ success: boolean; added: number }> => {
        const response = await authFetch(`${API_BASE_URL}/concerns/${concernId}/tasks`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ task_ids })
        });
        if (!response.ok) throw new Error('Failed to add tasks to concern');
        return response.json();
    },

    // Remove a task link from concern
    removeConcernTask: async (linkId: number): Promise<void> => {
        const response = await authFetch(`${API_BASE_URL}/concern-tasks/${linkId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to remove task from concern');
    },

    // ==========================================
    // RESOURCE PLANNING
    // ==========================================

    // Calculate resource plan for selected tasks
    calculateResourcePlan: async (projectId: string, taskIds: string[]): Promise<ResourcePlanResult> => {
        const response = await authFetch(`${API_BASE_URL}/resource-plan/calculate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                project_id: projectId, 
                task_ids: taskIds.map(id => parseInt(id)) 
            })
        });
        if (!response.ok) throw new Error('Failed to calculate resource plan');
        return response.json();
    },

    // Suggest tasks that fit within resource limits
    suggestTasksForResources: async (
        projectId: string, 
        resourceLimits: Record<string, number>,
        excludeTaskIds: string[] = []
    ): Promise<ResourceSuggestionResult> => {
        const response = await authFetch(`${API_BASE_URL}/resource-plan/suggest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                project_id: projectId, 
                resource_limits: resourceLimits,
                exclude_task_ids: excludeTaskIds.map(id => parseInt(id))
            })
        });
        if (!response.ok) throw new Error('Failed to suggest tasks');
        return response.json();
    },

    // Batch calculate manpower for all tasks in a project
    batchCalculateManpower: async (projectId: string): Promise<BatchManpowerResult> => {
        const response = await authFetch(`${API_BASE_URL}/tasks/batch-calculate-manpower`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ project_id: projectId })
        });
        if (!response.ok) throw new Error('Failed to batch calculate manpower');
        return response.json();
    },

    // ==========================================
    // ADMIN TASKS (the "Tasks" view — mirror of the mobile task flow
    // but across every task in a project). See backend/routes/admin_tasks.js.
    // ==========================================

    adminListTasks: async (filters: AdminTaskFilters): Promise<AdminTaskListResponse> => {
        const params = new URLSearchParams();
        if (filters.projectId)  params.append('project_id',  filters.projectId);
        if (filters.status)     params.append('status',      filters.status);
        if (filters.assignee)   params.append('assignee',    String(filters.assignee));
        if (filters.assignment) params.append('assignment',  filters.assignment);
        if (filters.buildingId)    params.append('building_id',    filters.buildingId);
        if (filters.categoryId)    params.append('category_id',    filters.categoryId);
        if (filters.subcategoryId) params.append('subcategory_id', filters.subcategoryId);
        if (filters.floorId)       params.append('floor_id',       filters.floorId);
        if (filters.unitId)        params.append('unit_id',        filters.unitId);
        if (filters.search)        params.append('search',         filters.search);
        if (filters.scope)      params.append('scope',       filters.scope);
        if (filters.weekStart)  params.append('week_start',  filters.weekStart);
        if (filters.milestoneId)         params.append('milestone_id',        filters.milestoneId);
        if (filters.includeMilestones)   params.append('include_milestones',  '1');
        if (filters.limit  != null) params.append('limit',  String(filters.limit));
        if (filters.offset != null) params.append('offset', String(filters.offset));

        const res = await authFetch(`${API_BASE_URL}/admin/tasks?${params.toString()}`);
        if (!res.ok) throw new Error('Failed to fetch admin tasks');
        return res.json();
    },

    // ==========================================
    // WBS / MILESTONE APIs (backend/routes/admin_wbs.js, migration 008)
    // ==========================================

    // Fetch the milestone tree for a project.
    //   flat=true  → every milestone in one payload (~1–2 MB for Harita,
    //                builds the whole tree client-side). Good default for
    //                medium projects.
    //   parentId='root' (or undefined) → only top-level milestones.
    //   parentId='<id>' → only immediate children of that milestone
    //                     (for lazy-expand UIs).
    adminGetWbsTree: async (
        projectId: string,
        opts?: { parentId?: string; flat?: boolean }
    ): Promise<WbsTreeResponse> => {
        const params = new URLSearchParams();
        if (opts?.flat) params.append('flat', '1');
        else if (opts?.parentId) params.append('parent_id', opts.parentId);
        const qs = params.toString();
        const res = await authFetch(
            `${API_BASE_URL}/admin/projects/${projectId}/wbs-tree${qs ? `?${qs}` : ''}`
        );
        if (!res.ok) throw new Error('Failed to fetch WBS tree');
        return res.json();
    },

    adminGetMilestone: async (milestoneId: string): Promise<WbsMilestone> => {
        const res = await authFetch(`${API_BASE_URL}/admin/milestones/${milestoneId}`);
        if (!res.ok) throw new Error('Failed to fetch milestone');
        return res.json();
    },

    // Paginated leaf tasks rolling up to this milestone.
    adminGetMilestoneTasks: async (
        milestoneId: string,
        opts?: { status?: TaskStatus; assignment?: 'any' | 'assigned' | 'unassigned'; limit?: number; offset?: number }
    ): Promise<{ milestoneId: string; total: number; limit: number; offset: number; tasks: any[] }> => {
        const params = new URLSearchParams();
        if (opts?.status)     params.append('status',     opts.status);
        if (opts?.assignment) params.append('assignment', opts.assignment);
        if (opts?.limit  != null) params.append('limit',  String(opts.limit));
        if (opts?.offset != null) params.append('offset', String(opts.offset));
        const res = await authFetch(
            `${API_BASE_URL}/admin/milestones/${milestoneId}/tasks?${params.toString()}`
        );
        if (!res.ok) throw new Error('Failed to fetch milestone tasks');
        return res.json();
    },

    // Leaves whose sub-tree carries no milestone rows — the ERP has not
    // tagged these paths yet (e.g. "Mockup" and parts of D-Building for
    // Harita). Surfaced so nothing falls out of the grouped view.
    adminGetUnassignedTasks: async (
        projectId: string,
        opts?: { limit?: number; offset?: number }
    ): Promise<{ projectId: number; total: number; limit: number; offset: number; tasks: any[] }> => {
        const params = new URLSearchParams();
        if (opts?.limit  != null) params.append('limit',  String(opts.limit));
        if (opts?.offset != null) params.append('offset', String(opts.offset));
        const qs = params.toString();
        const res = await authFetch(
            `${API_BASE_URL}/admin/projects/${projectId}/unassigned-tasks${qs ? `?${qs}` : ''}`
        );
        if (!res.ok) throw new Error('Failed to fetch unassigned tasks');
        return res.json();
    },

    // Milestone-centric browse (backend/routes/admin_milestones.js).
    //   from / to     ISO date strings; server defaults to today-30d / today+90d.
    //   buckets       one or more of overdue|in_progress|due_soon|upcoming|achieved|unscheduled
    //   parentId      restrict to descendants of this milestone (subtree scope)
    //   dateField     'finish' (default) or 'start' — which date the window applies to
    adminListMilestones: async (
        projectId: string,
        opts?: MilestoneBrowseFilters
    ): Promise<MilestoneBrowseResponse> => {
        const params = new URLSearchParams();
        if (opts?.from) params.append('from', opts.from);
        if (opts?.to)   params.append('to',   opts.to);
        if (opts?.buckets && opts.buckets.length) params.append('bucket', opts.buckets.join(','));
        if (opts?.parentId) params.append('parent_id', opts.parentId);
        if (opts?.buildingId) params.append('building_id', opts.buildingId);
        if (opts?.dateField) params.append('dateField', opts.dateField);
        if (opts?.leafOnly === false) params.append('leafOnly', '0');
        if (opts?.limit  != null) params.append('limit',  String(opts.limit));
        if (opts?.offset != null) params.append('offset', String(opts.offset));
        const qs = params.toString();
        const res = await authFetch(
            `${API_BASE_URL}/admin/projects/${projectId}/milestones${qs ? `?${qs}` : ''}`
        );
        if (!res.ok) throw new Error('Failed to fetch milestones');
        return res.json();
    },

    adminMilestoneSummary: async (
        projectId: string,
        opts?: Omit<MilestoneBrowseFilters, 'buckets' | 'limit' | 'offset'>
    ): Promise<MilestoneSummaryResponse> => {
        const params = new URLSearchParams();
        if (opts?.from) params.append('from', opts.from);
        if (opts?.to)   params.append('to',   opts.to);
        if (opts?.parentId) params.append('parent_id', opts.parentId);
        if (opts?.buildingId) params.append('building_id', opts.buildingId);
        if (opts?.dateField) params.append('dateField', opts.dateField);
        if (opts?.leafOnly === false) params.append('leafOnly', '0');
        const qs = params.toString();
        const res = await authFetch(
            `${API_BASE_URL}/admin/projects/${projectId}/milestones/summary${qs ? `?${qs}` : ''}`
        );
        if (!res.ok) throw new Error('Failed to fetch milestone summary');
        return res.json();
    },

    adminGetTaskDetail: async (taskId: string | number): Promise<AdminTaskDetailResponse> => {
        const res = await authFetch(`${API_BASE_URL}/admin/tasks/${taskId}/detail`);
        if (!res.ok) throw new Error('Failed to fetch task detail');
        const data = await res.json();
        if (!Array.isArray(data.documents)) data.documents = [];
        return data;
    },

    adminSetTaskStatus: async (
        taskId: string | number,
        status: 'In Progress' | 'On Hold' | 'Completed',
        opts?: {
            reportedBy?: number | null;
            // Required when status === 'On Hold'. Must match HoldReason literal.
            holdReason?: HoldReason;
            holdNotes?: string;
        },
    ): Promise<{ success: boolean; task: AdminTask | null }> => {
        const res = await authFetch(`${API_BASE_URL}/admin/tasks/${taskId}/status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                status,
                reported_by: opts?.reportedBy ?? null,
                hold_reason: opts?.holdReason ?? null,
                hold_notes:  opts?.holdNotes  ?? null,
            }),
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to update task status');
        return payload;
    },

    adminLogTaskProgress: async (
        taskId: string | number,
        data: {
            progressPercent?: number;
            quantityCompleted?: number;
            notes?: string;
            statusChange?: 'In Progress' | 'On Hold' | 'Completed' | 'Resumed';
            reportedBy?: number | null;
        },
    ): Promise<{ success: boolean; task: AdminTask | null }> => {
        const res = await authFetch(`${API_BASE_URL}/admin/tasks/${taskId}/progress`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                progress_percent:   data.progressPercent,
                quantity_completed: data.quantityCompleted,
                notes:              data.notes,
                status_change:      data.statusChange,
                reported_by:        data.reportedBy ?? null,
            }),
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to log progress');
        return payload;
    },

    // Create a new issue (concern) attached to a task. Mirrors the mobile
    // POST /api/mobile/tasks/:taskId/issues endpoint but with no JWT, so
    // the server stamps reported_by_name = 'Admin'.
    adminCreateTaskIssue: async (
        taskId: string | number,
        data: {
            title: string;
            description?: string;
            concernType?: IssueConcernType;
            priority?: IssuePriority;
            assignedTo?: number | null;
        },
    ): Promise<{ success: boolean; issue: AdminTaskIssue }> => {
        const res = await authFetch(`${API_BASE_URL}/admin/tasks/${taskId}/issues`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                title:        data.title,
                description:  data.description ?? null,
                concern_type: data.concernType ?? 'other',
                priority:     data.priority    ?? 'medium',
                assigned_to:  data.assignedTo  ?? null,
            }),
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to create issue');
        return payload;
    },

    // Move an issue through its lifecycle (open → acknowledged → resolved
    // → closed, or back). Resolution_notes is free text that surfaces in
    // the drawer's issue card.
    adminUpdateIssueStatus: async (
        issueId: number,
        data: { status: IssueStatus; resolutionNotes?: string },
    ): Promise<{ success: boolean; issue: AdminTaskIssue }> => {
        const res = await authFetch(`${API_BASE_URL}/admin/issues/${issueId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                status:            data.status,
                resolution_notes:  data.resolutionNotes,
            }),
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to update issue');
        return payload;
    },

    adminUnlinkTaskDocument: async (
        taskId: string | number,
        documentId: string,
    ): Promise<{ success: boolean }> => {
        const res = await authFetch(
            `${API_BASE_URL}/admin/tasks/${taskId}/documents/${documentId}`,
            { method: 'DELETE' },
        );
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to unlink document');
        return payload;
    },

    /** Project library rows (`/api/projects/:id/documents`) for linking into a task. */
    fetchProjectDocuments: async (projectId: string): Promise<ProjectLibraryDocument[]> => {
        const res = await authFetch(`${API_BASE_URL}/projects/${projectId}/documents`);
        if (!res.ok) throw new Error('Failed to load project documents');
        return res.json();
    },

    linkDocumentToTask: async (
        documentId: string,
        taskId: string | number,
        isMandatory = false,
    ): Promise<unknown> => {
        const res = await authFetch(`${API_BASE_URL}/documents/${documentId}/link-task`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ task_id: taskId, is_mandatory: isMandatory }),
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to link document');
        return payload;
    },

    adminListTaskContractors: async (taskId: string | number): Promise<AdminContractor[]> => {
        const res = await authFetch(`${API_BASE_URL}/admin/tasks/${taskId}/contractors`);
        if (!res.ok) throw new Error('Failed to load contractors');
        return res.json();
    },

    adminAddTaskContractor: async (
        taskId: string | number,
        body: { name: string; phone?: string; scope?: string },
    ): Promise<{ contractor: AdminContractor; linked: boolean }> => {
        const res = await authFetch(`${API_BASE_URL}/admin/tasks/${taskId}/contractors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to add contractor');
        return payload;
    },

    adminRemoveTaskContractor: async (
        taskId: string | number,
        contractorId: string,
    ): Promise<{ success: boolean }> => {
        const res = await authFetch(
            `${API_BASE_URL}/admin/tasks/${taskId}/contractors/${contractorId}`,
            { method: 'DELETE' },
        );
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to remove contractor');
        return payload;
    },

    adminListTaskAttendance: async (
        taskId: string | number,
        opts?: { from?: string; to?: string },
    ): Promise<AdminAttendanceRow[]> => {
        const params = new URLSearchParams();
        if (opts?.from) params.append('from', opts.from);
        if (opts?.to) params.append('to', opts.to);
        const qs = params.toString();
        const res = await authFetch(
            `${API_BASE_URL}/admin/tasks/${taskId}/attendance${qs ? `?${qs}` : ''}`,
        );
        if (!res.ok) throw new Error('Failed to load attendance');
        return res.json();
    },

    adminUpsertTaskAttendance: async (
        taskId: string | number,
        body: {
            contractor_id: string;
            attendance_date: string;
            skilled_count?: number;
            unskilled_count?: number;
            notes?: string;
        },
    ): Promise<{ row: AdminAttendanceRow }> => {
        const res = await authFetch(`${API_BASE_URL}/admin/tasks/${taskId}/attendance`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to save attendance');
        return payload;
    },

    adminDeleteAttendance: async (attendanceId: string): Promise<{ success: boolean }> => {
        const res = await authFetch(`${API_BASE_URL}/admin/attendance/${attendanceId}`, {
            method: 'DELETE',
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to delete attendance');
        return payload;
    },

    adminListMaterialRequests: async (taskId: string | number): Promise<AdminMaterialRequest[]> => {
        const res = await authFetch(`${API_BASE_URL}/admin/tasks/${taskId}/material-requests`);
        if (!res.ok) throw new Error('Failed to load material requests');
        return res.json();
    },

    adminCreateMaterialRequest: async (
        taskId: string | number,
        body: {
            required_by?: string | null;
            notes?: string | null;
            items: Array<{ material_name: string; quantity: number; uom: string; notes?: string }>;
        },
    ): Promise<AdminMaterialRequest> => {
        const res = await authFetch(`${API_BASE_URL}/admin/tasks/${taskId}/material-requests`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to create request');
        return payload;
    },

    adminPatchMaterialRequest: async (
        requestId: string,
        body: {
            status: MaterialRequestStatus;
            bill_no?: string;
            items?: Array<{ id: string; quantity_issued: number }>;
        },
    ): Promise<AdminMaterialRequest> => {
        const res = await authFetch(`${API_BASE_URL}/admin/material-requests/${requestId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to update request');
        return payload;
    },

    adminDeleteMaterialRequest: async (requestId: string): Promise<{ success: boolean }> => {
        const res = await authFetch(`${API_BASE_URL}/admin/material-requests/${requestId}`, {
            method: 'DELETE',
        });
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error || 'Failed to delete request');
        return payload;
    },

    adminListTaskMaterialsRollup: async (taskId: string | number): Promise<AdminMaterialRollupRow[]> => {
        const res = await authFetch(`${API_BASE_URL}/admin/tasks/${taskId}/materials`);
        if (!res.ok) throw new Error('Failed to load materials summary');
        return res.json();
    },
};

// ==========================================
// TYPES
// ==========================================

export interface MonthlyPlan {
    id: number;
    project_id: number;
    year: number;
    month: number;
    name: string;
    status: 'draft' | 'published' | 'closed';
    description?: string;
    created_at: string;
    published_at?: string;
    closed_at?: string;
    total_tasks?: number;
    completed_tasks?: number;
    active_tasks?: number;
    rolled_over_tasks?: number;
    from_rollover?: number;
    from_master?: number;
}

export interface MonthlyPlanTask {
    id: number;
    monthly_plan_id: number;
    task_id: number;
    source: 'master' | 'rollover';
    source_plan_id?: number;
    start_progress: number;
    end_progress?: number;
    status: 'active' | 'completed' | 'rolled_over';
    notes?: string;
    wbs_code: string;
    task_name: string;
    task_status: string;
    building_name?: string;
    category_name?: string;
    floor_name?: string;
    source_year?: number;
    source_month?: number;
}

export interface MonthlyPlanDetail extends MonthlyPlan {
    project_name: string;
    tasks: MonthlyPlanTask[];
    stats: {
        total: number;
        completed: number;
        active: number;
        rolled_over: number;
        from_rollover: number;
        from_master: number;
    };
}

export interface CreateMonthlyPlanData {
    year: number;
    month: number;
    name?: string;
    description?: string;
}

export interface WeeklyPlan {
    id: number;
    monthly_plan_id: number;
    week_number: number;
    start_date: string;
    end_date: string;
    name?: string;
    status: 'draft' | 'published' | 'closed';
    created_at: string;
    published_at?: string;
    closed_at?: string;
    total_tasks?: number;
    completed_tasks?: number;
    // Computed fields for display
    month_name?: string;
    year?: number;
}

export interface CreateWeeklyPlanData {
    monthly_plan_id: number;
    week_number: number;
    start_date: string;
    end_date: string;
    name?: string;
}

export interface WeeklyPlanTask {
    weekly_plan_task_id: number;
    weekly_status: string;
    added_to_week: string;
    monthly_plan_task_id: number;
    task_id: number;
    monthly_status: string;
    source: string;
    start_progress: number;
    end_progress: number;
    wbs_code: string;
    task_name: string;
    task_status: string;
    planned_start_date: string;
    planned_finish_date: string;
    quantity: number;
    uom: string;
    estimated_cost: number;
    building_name: string;
    category_name: string;
    floor_name: string;
    unit_name: string;
}

export interface WeeklyPlanDetail extends WeeklyPlan {
    month: number;
    project_id: number;
    month_name: string;
    tasks: WeeklyPlanTask[];
    stats: {
        total: number;
        completed: number;
        active: number;
    };
}

// ==========================================
// CONCERNS TYPES
// ==========================================

export type ConcernType = 'drawing_missing' | 'resource_constraint' | 'dependency_issue' | 'schedule_risk' | 'other';
export type ConcernStatus = 'open' | 'acknowledged' | 'resolved' | 'closed';
export type ConcernPriority = 'low' | 'medium' | 'high' | 'critical';

export interface Concern {
    id: number;
    project_id: number;
    monthly_plan_id?: number;
    concern_type: ConcernType;
    title: string;
    description?: string;
    status: ConcernStatus;
    priority: ConcernPriority;
    created_by?: number;
    created_at: string;
    resolved_at?: string;
    resolution_notes?: string;
    // Joined fields
    plan_month?: number;
    plan_year?: number;
    created_by_name?: string;
    task_count?: number;
}

export interface ConcernTask {
    link_id: number;
    task_id: number;
    activity_name: string;
    category: string;
    floor: string;
    unit_name: string;
    progress: number;
    status: string;
    building_name: string;
    work_stage_name: string;
}

export interface ConcernDetail extends Concern {
    tasks: ConcernTask[];
}

export interface CreateConcernData {
    concern_type: ConcernType;
    title: string;
    description?: string;
    priority?: ConcernPriority;
    monthly_plan_id?: number;
    created_by?: number;
    task_ids?: number[];
}

// Incomplete task for rollover selection
export interface IncompletePlanTask {
    plan_task_id: number;
    task_id: number;
    plan_status: string;
    source: string;
    start_progress: number;
    task_name: string;
    wbs_code: string;
    quantity: number;
    uom: string;
    task_status: string;
    progress: number | null;
    building_name: string;
    category_name: string;
    floor_name: string;
    unit_name: string;
}

// ==========================================
// RESOURCE PLANNING TYPES
// ==========================================

export interface WeeklyResourceData {
    week: number;
    startDate: string;
    endDate: string;
    taskCount: number;
    tasks: Array<{
        id: number;
        name: string;
        category: string;
        floor: string;
    }>;
    resources: Record<string, number>;
}

export interface ResourceTotals {
    [labor: string]: {
        total: number;
        peak: number;
        peakWeek: number;
    };
}

export interface ResourcePlanResult {
    taskCount: number;
    weekCount: number;
    dateRange: {
        start: string;
        end: string;
    };
    weeks: WeeklyResourceData[];
    totals: ResourceTotals;
}

export interface ResourceSuggestionResult {
    suggested_task_ids: number[];
    total_available: number;
    total_suggested: number;
    resource_limits: Record<string, number>;
    peak_usage: Record<string, number>;
}

export interface BatchManpowerResult {
    success: boolean;
    total_tasks: number;
    updated: number;
    skipped: number;
    message: string;
}

// ==========================================
// ADMIN TASKS TYPES
// ==========================================

export type TaskStatus = 'Not Started' | 'In Progress' | 'On Hold' | 'Completed';

// Hold reason categories. Kept in sync with:
//   backend/migrations/007_hold_reasons.sql (DB CHECK constraint)
//   backend/routes/admin_tasks.js HOLD_REASONS
//   frontend HoldReasonModal
export type HoldReason = 'Material' | 'Manpower' | 'Drawing' | 'External' | 'Machine' | 'Others';
export const HOLD_REASONS: readonly HoldReason[] = [
    'Material', 'Manpower', 'Drawing', 'External', 'Machine', 'Others',
] as const;

export interface AdminTaskFilters {
    projectId?: string;
    status?: TaskStatus;
    assignee?: number | string;
    assignment?: 'any' | 'assigned' | 'unassigned';
    // Location-tree filters. Driven by the shared ProjectTreePanel in the
    // Tasks view — any selected node collapses into this filter shape.
    buildingId?: string;
    categoryId?: string;
    subcategoryId?: string;
    floorId?: string;
    unitId?: string;
    search?: string;
    scope?: 'all' | 'week' | 'month';
    weekStart?: string;       // YYYY-MM-DD, Monday
    // Migration 008: restrict the list to leaves rolling up to this
    // milestone (uses tasks.milestone_task_id).
    milestoneId?: string;
    // Default excludes is_milestone=TRUE rows. Pass true to include them
    // (useful for audit views that need to show the group/summary nodes).
    includeMilestones?: boolean;
    limit?: number;
    offset?: number;
}

export interface AdminTask {
    id: string;
    wbsCode: string | null;
    name: string;
    projectId: string;
    projectName: string;
    building: string | null;
    floor: string | null;
    unit: string | null;
    series: string | null;
    category: string | null;
    subCategory: string | null;
    quantity: number;
    quantityCompleted: number;
    uom: string | null;
    progressPercent: number;
    status: TaskStatus;
    plannedStart: string | null;
    plannedFinish: string | null;
    actualStart: string | null;
    actualFinish: string | null;
    monthlyPlanDate: string | null;
    weeklyPlanDate: string | null;
    assignedEngineerId: number | null;
    assignedEngineerName: string | null;
    openIssuesCount: number;
    // Migration 007: current hold context (null unless status = 'On Hold').
    currentHoldReason: HoldReason | null;
    currentHoldNotes: string | null;
    currentHoldAt: string | null;
    // Migration 008: milestone / WBS hierarchy.
    // `isMilestone` marks ERP group/summary rows (not leaf work items).
    // `milestoneTaskId` + `milestoneName` point at the nearest ancestor
    // milestone — this is the "which milestone does this task roll up to?"
    // surface for group-by-milestone views and progress badges.
    isMilestone: boolean;
    parentTaskId: string | null;
    milestoneTaskId: string | null;
    milestoneName: string | null;
    milestoneWbsCode: string | null;
    /** Rows from `get_documents_for_task` (list query); 0 when not selected. */
    linkedDocsCount: number;
    /** Material requests in `requested` or `approved` (migration 011). */
    openMaterialRequestsCount?: number;
    /** Sum of skilled + unskilled headcount logged for CURRENT_DATE (migration 011). */
    labourHeadcountToday?: number;
}

export interface ProjectLibraryDocument {
    id: string;
    title: string;
    document_type: string;
    document_number: string | null;
    revision: string | null;
    file_name: string;
    file_path: string;
    category_name: string | null;
}

export interface AdminContractor {
    id: string;
    name: string;
    phone: string | null;
    scope: string | null;
    added_at: string;
}

export interface AdminAttendanceRow {
    id: string;
    attendance_date: string;
    contractor_id: string;
    contractor_name: string;
    skilled_count: number;
    unskilled_count: number;
    notes: string | null;
    updated_at: string;
}

export type MaterialRequestStatus =
    | 'requested'
    | 'approved'
    | 'issued'
    | 'received'
    | 'cancelled';

export const MATERIAL_REQUEST_STATUSES: MaterialRequestStatus[] = [
    'requested', 'approved', 'issued', 'received', 'cancelled',
];

export const COMMON_MATERIAL_UOM = [
    'bags', 'kg', 'm3', 'nos', 'm', 'L', 'tonnes', 'sqm', 'cum',
] as const;

export interface AdminMaterialRequestItem {
    id: string;
    material_name: string;
    quantity: number;
    uom: string;
    quantity_issued: number;
    notes: string | null;
}

export interface AdminMaterialRequest {
    id: string;
    task_id: string;
    project_id: string;
    request_code: string;
    status: MaterialRequestStatus;
    bill_no: string | null;
    required_by: string | null;
    notes: string | null;
    requested_at: string;
    approved_at: string | null;
    issued_at: string | null;
    received_at: string | null;
    cancelled_at: string | null;
    items: AdminMaterialRequestItem[];
}

export interface AdminMaterialRollupRow {
    material_name: string;
    uom: string;
    quantity_requested: number;
    quantity_issued: number;
    last_issued_at: string | null;
}

// ==========================================
// WBS / MILESTONE TYPES (migration 008)
// ==========================================

export interface WbsMilestone {
    id: string;
    treeId: string;
    name: string;
    parentId: string | null;
    status: TaskStatus;
    plannedStart: string | null;
    plannedFinish: string | null;
    actualStart: string | null;
    actualFinish: string | null;
    // Rollup stats across all leaf tasks under this milestone's sub-tree.
    leafCount: number;
    completedCount: number;
    inProgressCount: number;
    onHoldCount: number;
    notStartedCount: number;
    progressPercent: number;
    achieved: boolean;
    achievedOn: string | null;
    // Number of immediate child milestones (for lazy-expand UIs).
    childMilestoneCount?: number;
    // Only populated by the single-milestone endpoint.
    ancestors?: Array<{ id: string; treeId: string; name: string }>;
}

export interface WbsTreeResponse {
    projectId: number;
    milestones: WbsMilestone[];
}

// ------------------------------------------------------------------
// Milestone browse (backend/routes/admin_milestones.js)
// ------------------------------------------------------------------

export type MilestoneBucket =
    | 'overdue'
    | 'in_progress'
    | 'due_soon'
    | 'upcoming'
    | 'achieved'
    | 'unscheduled';

export interface MilestoneBrowseFilters {
    from?: string;
    to?: string;
    buckets?: MilestoneBucket[];
    parentId?: string;
    buildingId?: string;
    dateField?: 'finish' | 'start';
    leafOnly?: boolean;
    limit?: number;
    offset?: number;
}

export interface MilestoneBrowseRow {
    id: string;
    treeId: string;
    name: string;
    parentMilestoneId: string | null;
    parentMilestoneName: string | null;
    parentMilestoneTreeId: string | null;
    // Root→leaf ancestors (milestones only), excluding the row itself.
    breadcrumb: Array<{ id: string; name: string; treeId: string }>;
    status: TaskStatus | null;
    bucket: MilestoneBucket;
    plannedStart: string | null;
    plannedFinish: string | null;
    actualStart: string | null;
    actualFinish: string | null;
    leafCount: number;
    completedCount: number;
    inProgressCount: number;
    onHoldCount: number;
    notStartedCount: number;
    progressPercent: number;
    achieved: boolean;
    achievedOn: string | null;
    // Ids of every leaf (is_milestone=FALSE) task rolling up to this milestone.
    leafTaskIds: string[];
}

export interface MilestoneBrowseResponse {
    projectId: number;
    from: string;
    to: string;
    dateField: 'finish' | 'start';
    leafOnly: boolean;
    buckets: MilestoneBucket[];
    total: number;
    limit: number;
    offset: number;
    milestones: MilestoneBrowseRow[];
}

export interface MilestoneSummaryResponse {
    projectId: number;
    from: string;
    to: string;
    dateField: 'finish' | 'start';
    leafOnly: boolean;
    counts: Record<MilestoneBucket, number> & { total: number };
}

export interface AdminTaskListResponse {
    total: number;
    limit: number;
    offset: number;
    tasks: AdminTask[];
}

export interface AdminTaskHistoryEntry {
    id: string;
    progress_percent: number | null;
    quantity_completed: string | number | null;
    status_change: string | null;
    notes: string | null;
    photo_urls: string[] | null;
    reported_at: string;
    reported_by_name: string;     // 'Admin' when null on server
    hold_reason: HoldReason | null;
    hold_notes: string | null;
}

// Mirrors the DB CHECK constraints on `concerns` (migration
// 001_monthly_plans.sql). When we extend the enums server-side, update
// these literal types too — the modal dropdown iterates them.
export type IssueConcernType =
    | 'drawing_missing'
    | 'resource_constraint'
    | 'dependency_issue'
    | 'schedule_risk'
    | 'material_delay'
    | 'manpower_shortage'
    | 'weather_impact'
    | 'other';

export type IssuePriority = 'low' | 'medium' | 'high' | 'critical';
export type IssueStatus   = 'open' | 'acknowledged' | 'resolved' | 'closed';

export const ISSUE_CONCERN_TYPES: IssueConcernType[] = [
    'drawing_missing', 'resource_constraint', 'dependency_issue',
    'schedule_risk', 'material_delay', 'manpower_shortage',
    'weather_impact', 'other',
];
export const ISSUE_PRIORITIES: IssuePriority[] = ['low', 'medium', 'high', 'critical'];

export interface AdminTaskIssue {
    id: number;
    title: string;
    description: string | null;
    concern_type: IssueConcernType;
    priority: IssuePriority;
    status: IssueStatus;
    created_at: string;
    resolved_at: string | null;
    reported_by_name: string | null;
}

// A single on-hold span derived from task_progress_log. The backend walks
// every status_change row for the task, pairs each 'On Hold' event with the
// next status change, and leaves the final episode open (ongoing=true) when
// the task is still On Hold today. Delay in days is computed client-side so
// the "ongoing" count ticks against the user's own clock.
export interface AdminTaskHoldEpisode {
    id: string;
    hold_reason: HoldReason | null;
    hold_notes: string | null;
    started_at: string;
    started_by_name: string;
    ended_at: string | null;
    ended_by_status: string | null;   // 'In Progress' | 'Completed' | 'On Hold' | null
    ended_by_name: string | null;
    ongoing: boolean;
}

/** One row from `get_documents_for_task` (admin task detail). */
export interface AdminTaskLinkedDocument {
    document_id: string;
    document_type: string;
    title: string;
    document_number: string | null;
    revision: string | null;
    file_name: string;
    file_path: string;
    file_type: string | null;
    category_name: string | null;
    is_mandatory: boolean;
    match_type: 'direct' | 'floor' | 'category' | 'building' | 'project';
}

export interface AdminTaskDetailResponse {
    task: AdminTask;
    history: AdminTaskHistoryEntry[];
    issues: AdminTaskIssue[];
    holdEpisodes: AdminTaskHoldEpisode[];
    documents: AdminTaskLinkedDocument[];
}
